/**
 * Simple wrapper for wretch that takes care of things like cookies, content type, etc.
 *
 *
 * @author Nick Dreckshage <ndreckshage@wayfair.com>
 * @author Ryan Kehoe <rkehoe@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
/* eslint-env browser */

import realWretch from 'wretch';
import URLSearchParams from '@ungap/url-search-params';

const CLEAR_CACHE_QUERY_KEY = 'clearcache';
const CLEAR_CACHE_FALSY_VALUES = ['0', 'false', 'off'];
const METHOD_POST = 'POST';
const CONTENT_TYPE_JSON = 'application/json';
const CONTENT_TYPE = 'Content-Type';
const CSRF_TOKEN_PARAM = '_csrf_token';

const txidMiddleware = transactionID => next => (url, opts) => {
  const nextOpts = {
    ...opts,
    headers: {...opts.headers, 'X-Parent-TXID': transactionID},
  };

  return Promise.resolve(next(url, nextOpts));
};

const getURLQueryDelimiter = (url = '') => (url.indexOf('?') > -1 ? '&' : '?');

const normalizeQueryString = (param = '') =>
  param.toLocaleLowerCase().replace(/"/g, '');

const getClearCacheParamValue = (value = '') => {
  const normalizedValue = normalizeQueryString(value);
  return CLEAR_CACHE_FALSY_VALUES.indexOf(normalizedValue) === -1
    ? normalizedValue
    : false;
};

export const clearCacheMiddleware = next => (url, opts) => {
  // If url already has clearcache query param - bail out.
  if (url.includes(CLEAR_CACHE_QUERY_KEY)) {
    return Promise.resolve(next(url, opts));
  }

  // Get query delimiter (? or &)
  const queryDelimiter = getURLQueryDelimiter(url);

  // get clear cache val from window
  const query = new URLSearchParams(window.location.search);
  const shouldClearCache = query.get(CLEAR_CACHE_QUERY_KEY);

  // if query param is set, get encoded clear cache value
  const encodedClearCacheValue = shouldClearCache
    ? encodeURIComponent(getClearCacheParamValue(shouldClearCache))
    : false;

  return Promise.resolve(
    next(
      encodedClearCacheValue
        ? `${url}${queryDelimiter}${CLEAR_CACHE_QUERY_KEY}=${encodedClearCacheValue}`
        : url,
      opts
    )
  );
};

const csrfTokenMiddleware = next => (url, opts) => {
  if (
    opts.method === METHOD_POST &&
    window.__csrfToken__ &&
    opts.headers[CONTENT_TYPE] === CONTENT_TYPE_JSON
  ) {
    try {
      const body = JSON.parse(opts.body);
      const nextOpts = {
        ...opts,
        body: JSON.stringify({
          ...body,
          [CSRF_TOKEN_PARAM]: window.__csrfToken__,
        }),
      };
      return Promise.resolve(next(url, nextOpts));
    } catch (e) {
      // if we are unable to parse the body, fallback to no middleware
      return Promise.resolve(next(url, opts));
    }
  }
  // if not a POST request, necessary data is missing, or content-type is not json, fallback to no middleware
  return Promise.resolve(next(url, opts));
};

export const wretch = (url, opts = {}) => {
  if (typeof window === 'undefined') {
    throw new Error('@wayfair/wretch cannot be used on the server.');
  }
  if (!window.__transactionID__) {
    throw new Error(
      'No transactionID for @wayfair/wretch. Be sure to properly set up your application using the @wayfair/core-library-bundle lib.'
    );
  }

  const middlewares = [
    clearCacheMiddleware,
    txidMiddleware(window.__transactionID__),
  ];

  return realWretch(url)
    .polyfills({URLSearchParams})
    .middlewares(
      opts.useCSRFToken ? middlewares.concat(csrfTokenMiddleware) : middlewares
    )
    .defaults({
      // include cookies so that we return user specific data
      credentials: 'include',
      headers: {
        // php endpoints require these fields to appropriately return json
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
};

export default wretch;
